import React, { ImgHTMLAttributes } from 'react';
import ImgixComponent, { SharedImigixAndSourceProps } from 'react-imgix';

import NoPhotoImg from 'img-src/static/nophotos.png';
import { isValidImgixSource } from './isValidImgixSource';
import { useImgix } from './useImgix';

type ImgixSizes = Omit<SharedImigixAndSourceProps, 'width' | 'height'> & {
  sizes: string;
};

type ImgixWidth = Omit<SharedImigixAndSourceProps, 'sizes'> & {
  width: number;
};

export type ImgixProps = (ImgixSizes | ImgixWidth) & {
  fallbackSrc?: string;
};

export const Imgix: React.FC<ImgixProps> = props => {
  const { transformImageURL } = useImgix();
  const [fallback, setFallback] = React.useState<boolean>(false);

  const showFallback = React.useCallback(() => {
    setFallback(true);
  }, [setFallback]);

  const { src = NoPhotoImg, fallbackSrc = NoPhotoImg } = props;
  if (!props.src || !isValidImgixSource(props.src) || fallback) {
    return (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img
        {...(props.htmlAttributes as ImgHTMLAttributes<HTMLImageElement>)}
        data-test-locator={
          props?.htmlAttributes?.['data-test-locator'] || 'imgix'
        }
        alt={
          (props?.htmlAttributes as ImgHTMLAttributes<HTMLImageElement>)?.alt ||
          ''
        }
        sizes={(props as ImgixSizes).sizes}
        width={(props as ImgixWidth).width}
        className={props.className}
        src={fallback ? fallbackSrc : src || fallbackSrc}
        onError={showFallback}
      />
    );
  }

  return (
    <ImgixComponent
      {...props}
      htmlAttributes={{
        'data-test-locator': 'imgix',
        onError: showFallback,
        ...props.htmlAttributes,
      }}
      src={transformImageURL(src)}
    />
  );
};
